import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import {
    Alert,
    alpha,
    Box,
    Chip,
    lighten,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { AdvancedMarker, Map, useMap } from '@vis.gl/react-google-maps';
import React, { useMemo, useState } from 'react';
import { theme } from '../../../shared/theme';
import { capitalizeName } from '../../../shared/utils';
import { VehiclePosition } from '../../types/vehicles';
import { useVehicleTracking } from '../Experiences/Vehicles/useVehicleTracking';

const DEFAULT_CENTER = { lat: -33.865143, lng: 151.2099 };

export default function DeviceMapView() {
    const isTablet = useMediaQuery('(max-width: 770px)');
    const map = useMap();
    const { vehicleList, error: trackingError } = useVehicleTracking();

    const [statusFilter, setStatusFilter] = useState<
        'all' | 'available' | 'reserved'
    >('all');
    const [selectedId, setSelectedId] = useState<number | null>(null);

    const filteredVehicleList = useMemo(
        () =>
            statusFilter === 'all'
                ? vehicleList
                : vehicleList.filter(
                      (v: any) => v.module.status === statusFilter,
                  ),
        [vehicleList, statusFilter],
    );

    const handleVehicleClick = (vehicle: VehiclePosition) => {
        setSelectedId(vehicle.module.id);
        map?.panTo({
            lat: vehicle.position.latitude,
            lng: vehicle.position.longitude,
        });
        map?.setZoom(15);
    };

    const firstPosition = vehicleList[0]?.position;
    const mapDefaultCenter = firstPosition
        ? { lat: firstPosition.latitude, lng: firstPosition.longitude }
        : DEFAULT_CENTER;

    enum Status {
        Available = 'available',
        Reserved = 'reserved',
        All = 'all',
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {trackingError && (
                <Alert severity='error' sx={{ borderRadius: 2 }}>
                    Live vehicle tracking is unavailable (connection error).{' '}
                    {trackingError}
                </Alert>
            )}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isTablet ? 'column' : 'row',
                    gap: 1,
                    height: '600px',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        maxWidth: !isTablet ? '300px' : '700px',
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                        {[Status.All, Status.Available, Status.Reserved].map(
                            (filter) => (
                                <Chip
                                    key={filter}
                                    label={capitalizeName(filter)}
                                    size='small'
                                    onClick={() => setStatusFilter(filter)}
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: 11,
                                        bgcolor:
                                            statusFilter === filter
                                                ? alpha(
                                                      theme.palette.primary
                                                          .main,
                                                      0.15,
                                                  )
                                                : theme.palette.action.hover,
                                        color:
                                            statusFilter === filter
                                                ? theme.palette.primary.main
                                                : theme.palette.text.secondary,
                                        '&:hover': {
                                            bgcolor: alpha(
                                                theme.palette.primary.main,
                                                0.1,
                                            ),
                                        },
                                    }}
                                />
                            ),
                        )}
                    </Box>
                    <Box
                        sx={{
                            maxHeight: isTablet ? '200px' : 'auto',
                            width: '100%',
                            overflowY: 'auto',
                            flex: 1,
                            borderRadius: 2,
                            '&::-webkit-scrollbar': {
                                width: 8,
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: theme.palette.form.background,
                                borderRadius: 999,
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: theme.palette.custom.grey,
                                borderRadius: 999,
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                                backgroundColor: theme.palette.text.secondary,
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                            }}
                        >
                            {filteredVehicleList.length === 0 ? (
                                <Box
                                    sx={{
                                        py: 3,
                                        px: 1,
                                        textAlign: 'center',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            lineHeight: 1.5,
                                            color: theme.palette.text.secondary,
                                        }}
                                    >
                                        {vehicleList.length === 0
                                            ? 'No vehicles to show yet. Live positions will appear here when data is available.'
                                            : 'No vehicles match this filter. Try All, Available, or Reserved.'}
                                    </Typography>
                                </Box>
                            ) : (
                                filteredVehicleList.map((item) => {
                                    const isSelected =
                                        selectedId === item.module.id;
                                    return (
                                        <Box
                                            key={item.module.id}
                                            onClick={() => {
                                                handleVehicleClick(item);
                                            }}
                                            sx={{
                                                borderRadius: 2,
                                                border: `1px dashed ${lighten(theme.palette.primary.main, 0.5)}`,
                                                boxShadow: `3px 3px 11px 0px ${lighten(theme.palette.custom.grey, 0.7)}`,
                                                width: '100%',
                                                p: 1,
                                                bgcolor: isSelected
                                                    ? alpha(
                                                          theme.palette.primary
                                                              .main,
                                                          0.05,
                                                      )
                                                    : 'transparent',
                                                '&:hover': {
                                                    backgroundColor: alpha(
                                                        theme.palette.primary
                                                            .main,
                                                        0.05,
                                                    ),
                                                },
                                                transition: 'all 0.2s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: '50%',
                                                    bgcolor: isSelected
                                                        ? alpha(
                                                              theme.palette
                                                                  .primary.main,
                                                              0.15,
                                                          )
                                                        : theme.palette.action
                                                              .hover,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <DirectionsCarIcon
                                                    sx={{
                                                        fontSize: 18,
                                                    }}
                                                />
                                            </Box>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent:
                                                            'space-between',
                                                    }}
                                                >
                                                    <Box sx={{ width: '100%' }}>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                justifyContent:
                                                                    'space-between',
                                                                width: '100%',
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    alignItems:
                                                                        'center',
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontWeight: 700,
                                                                        fontSize: 13,
                                                                        lineHeight: 1.3,
                                                                        overflow:
                                                                            'hidden',
                                                                        textOverflow:
                                                                            'ellipsis',
                                                                    }}
                                                                >
                                                                    {
                                                                        item
                                                                            .module
                                                                            .name
                                                                    }
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                gap: 0.75,
                                                                mt: 0.5,
                                                            }}
                                                        >
                                                            <Chip
                                                                label={
                                                                    item.module
                                                                        .status ===
                                                                    'available'
                                                                        ? 'Available'
                                                                        : 'Reserved'
                                                                }
                                                                size='small'
                                                                sx={{
                                                                    height: 20,
                                                                    fontSize: 10,
                                                                    fontWeight: 600,
                                                                    bgcolor:
                                                                        item
                                                                            .module
                                                                            .status ===
                                                                        'available'
                                                                            ? alpha(
                                                                                  theme
                                                                                      .palette
                                                                                      .success
                                                                                      .main,
                                                                                  0.15,
                                                                              )
                                                                            : alpha(
                                                                                  theme
                                                                                      .palette
                                                                                      .warning
                                                                                      .main,
                                                                                  0.15,
                                                                              ),
                                                                    color:
                                                                        item
                                                                            .module
                                                                            .status ===
                                                                        'available'
                                                                            ? theme
                                                                                  .palette
                                                                                  .success
                                                                                  .dark
                                                                            : theme
                                                                                  .palette
                                                                                  .warning
                                                                                  .dark,
                                                                }}
                                                            />
                                                            {item.module
                                                                .bookingId && (
                                                                <Typography
                                                                    variant='body3'
                                                                    sx={{
                                                                        fontSize: 11,
                                                                        color: theme
                                                                            .palette
                                                                            .text
                                                                            .secondary,
                                                                    }}
                                                                >
                                                                    Booking #
                                                                    {
                                                                        item
                                                                            .module
                                                                            .bookingId
                                                                    }
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })
                            )}
                        </Box>
                    </Box>
                </Box>
                <Box
                    sx={{
                        borderRadius: 3,
                        overflow: 'hidden',
                        flex: 1,
                    }}
                >
                    <Map
                        defaultZoom={12}
                        defaultCenter={mapDefaultCenter}
                        mapId='9064210e52cc2181f0dc0a4e'
                        style={{ height: '100%', width: '100%' }}
                        disableDefaultUI
                        fullscreenControl
                        gestureHandling='greedy'
                    >
                        {filteredVehicleList.map((item) => {
                            const isSelected = selectedId === item.module.id;
                            return (
                                <AdvancedMarker
                                    key={item.module.id}
                                    position={{
                                        lat: item.position.latitude,
                                        lng: item.position.longitude,
                                    }}
                                    onClick={() => handleVehicleClick(item)}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            transition: 'transform 0.2s',
                                            transform: isSelected
                                                ? 'scale(1.25)'
                                                : 'scale(1)',
                                            gap: 0.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                bgcolor: isSelected
                                                    ? theme.palette.primary.main
                                                    : theme.palette.background
                                                          .default,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: isSelected
                                                    ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.6)}`
                                                    : `0 1px 4px ${alpha(theme.palette.primary.main, 0.5)}`,
                                            }}
                                        >
                                            <DirectionsCarIcon
                                                sx={{
                                                    fontSize: 16,
                                                    color: isSelected
                                                        ? theme.palette.primary
                                                              .contrastText
                                                        : undefined,
                                                }}
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                bgcolor:
                                                    theme.palette.background
                                                        .default,
                                                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.6)}`,
                                                px: 0.5,
                                                py: 0.25,
                                                borderRadius: 2,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {item.module.name}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: 8,
                                                    fontWeight: 600,
                                                    whiteSpace: 'nowrap',
                                                    textAlign: 'center',
                                                    color:
                                                        item.module.status ===
                                                        'available'
                                                            ? theme.palette
                                                                  .success.main
                                                            : theme.palette
                                                                  .warning.main,
                                                }}
                                            >
                                                {item.module.status ===
                                                'available'
                                                    ? 'Available'
                                                    : 'Reserved'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </AdvancedMarker>
                            );
                        })}
                    </Map>
                </Box>
            </Box>
        </Box>
    );
}
